import { useState, useCallback, useEffect, useMemo } from 'react';

export interface UseTableStateReturn {
  // Filter state
  globalFilterValue: string;
  setGlobalFilterValue: (value: string) => void;

  // Pagination state
  currentPageSize: number;
  setCurrentPageSize: (size: number) => void;
  currentPageRows: any[];
  setCurrentPageRows: (rows: any[]) => void;

  // Selection state
  selectedAppsForUpdate: Set<number>;
  setSelectedAppsForUpdate: (apps: Set<number>) => void;

  // Handlers
  handleMatrixPageChange: (e: any) => void;
  handleMatrixSelectionChange: (e: any) => void;

  // Select All Dialog state
  showSelectAllDialog: boolean;
  setShowSelectAllDialog: (show: boolean) => void;
  selectAllDialogData: {
    leadLogin: string;
    targetValue: boolean;
    visibleRows: any[];
    currentPageCount: number;
  } | null;
  setSelectAllDialogData: (data: any) => void;
}

interface UseTableStateProps {
  matrixTableRows: any[];
}

/**
 * Custom hook to manage table state
 * @param param0 - Props for the hook
 * @returns Table state and handlers
 */
export const useTableState = ({
  matrixTableRows,
}: UseTableStateProps): UseTableStateReturn => {
  // Filter state
  const [globalFilterValue, setGlobalFilterValue] = useState<string>('');

  // Pagination state
  const [currentPageSize, setCurrentPageSize] = useState<number>(25);
  const [currentPageRows, setCurrentPageRows] = useState<any[]>([]);

  // Selection state
  const [selectedAppsForUpdate, setSelectedAppsForUpdate] = useState<
    Set<number>
  >(new Set());

  // Effect to auto-select all applications when table data changes
  useEffect(() => {
    if (matrixTableRows.length > 0) {
      const allAppIds = new Set<number>(
        matrixTableRows.map(row => row.applicationId as number),
      );
      setSelectedAppsForUpdate(allAppIds);
    } else {
      setSelectedAppsForUpdate(new Set());
    }
  }, [matrixTableRows]);

  // Select All Dialog state
  const [showSelectAllDialog, setShowSelectAllDialog] =
    useState<boolean>(false);
  const [selectAllDialogData, setSelectAllDialogData] = useState<{
    leadLogin: string;
    targetValue: boolean;
    visibleRows: any[];
    currentPageCount: number;
  } | null>(null);

  // Memoized page change handler
  const handleMatrixPageChange = useCallback(
    (e: any) => {
      setCurrentPageSize(e.rows);
      // Calculate current page rows
      const startIndex = e.first;
      const endIndex = startIndex + e.rows;
      const currentRows = matrixTableRows.slice(startIndex, endIndex);
      setCurrentPageRows(currentRows);
    },
    [matrixTableRows],
  );

  // Memoized selection change handler
  const handleMatrixSelectionChange = useCallback((e: any) => {
    const selectedRows = e.value || [];
    const selectedIds = new Set<number>(
      selectedRows.map((row: any) => row.applicationId as number),
    );
    setSelectedAppsForUpdate(selectedIds);
  }, []);

  return useMemo(
    () => ({
      // Filter state
      globalFilterValue,
      setGlobalFilterValue,

      // Pagination state
      currentPageSize,
      setCurrentPageSize,
      currentPageRows,
      setCurrentPageRows,

      // Selection state
      selectedAppsForUpdate,
      setSelectedAppsForUpdate,

      // Handlers
      handleMatrixPageChange,
      handleMatrixSelectionChange,

      // Select All Dialog state
      showSelectAllDialog,
      setShowSelectAllDialog,
      selectAllDialogData,
      setSelectAllDialogData,
    }),
    [
      globalFilterValue,
      setGlobalFilterValue,
      currentPageSize,
      setCurrentPageSize,
      currentPageRows,
      setCurrentPageRows,
      selectedAppsForUpdate,
      setSelectedAppsForUpdate,
      handleMatrixPageChange,
      handleMatrixSelectionChange,
      showSelectAllDialog,
      setShowSelectAllDialog,
      selectAllDialogData,
      setSelectAllDialogData,
    ],
  );
};
